if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

let isReordering = false;

// Function to re-run reordering after variant changes
function handleVariantChange() {
  if (isReordering) return; // Prevent multiple calls
  
  isReordering = true;
  setTimeout(() => {
    reorderMediaBasedOnReferrer();
    isReordering = false; // Reset flag after reordering
  }, 300);
}

// Listen for variant changes
function setupVariantChangeListener() {
  // Method 1: Listen for variant form changes
  const variantSelectors = document.querySelectorAll('variant-selects, variant-radios, [name="id"]');
  variantSelectors.forEach(selector => {
    selector.addEventListener('change', handleVariantChange);
  });

  // Method 2: Listen for specific variant picker changes (more targeted)
  const variantPickers = document.querySelectorAll('variant-picker, product-form');
  variantPickers.forEach(picker => {
    picker.addEventListener('change', handleVariantChange);
  });

  // Method 3: Listen for variant updates via custom events (if your theme uses them)
  document.addEventListener('variant:change', handleVariantChange);
  document.addEventListener('product:variant-change', handleVariantChange);
}

function reorderMediaBasedOnReferrer() {
  const previousURL = document.referrer;
  
  // Check if previous URL indicates women's category
  const isFromWomen = previousURL.includes('/for-women') || 
                     previousURL.includes('/women') || 
                     previousURL.includes('/womens') || 
                     previousURL.includes('/collections/women') || 
                     previousURL.includes('/categories/women');
  
  // Check if previous URL indicates men's category  
  const isFromMen = previousURL.includes('/for-men') || 
                   previousURL.includes('/men') || 
                   previousURL.includes('/mens') || 
                   previousURL.includes('collections/mens') || 
                   previousURL.includes('/categories/men');

  if (!isFromWomen && !isFromMen) return; // No reordering needed

  // Get the media gallery
  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery) return;

  const viewer = mediaGallery.elements?.viewer;
  const thumbnails = mediaGallery.elements?.thumbnails;
  
  if (!viewer) return;

  // Get all media items
  const mediaItems = Array.from(viewer.querySelectorAll('[data-variant-grouping]')).filter(item => {
    return !item.classList.contains('hide-image');
  });
  const thumbnailItems = thumbnails ? Array.from(thumbnails.querySelectorAll('[data-target]')).filter(item => {
    return !item.classList.contains('hide-image');
  }) : [];

  // console.log(mediaItems, 'ini media items')

  // Function to check if media should be prioritized
  function shouldPrioritize(element, category) {
    const img = element.querySelector('img');
    if (!img) return false;
    
    const imgSrc = img.src || img.getAttribute('srcset') || '';
    const imgAlt = img.alt || '';
    
    // Combine src and alt for checking
    const imageInfo = (imgSrc + ' ' + imgAlt).toLowerCase();
    
    if (category === 'women') {
      return imageInfo.includes('women');
    } else if (category === 'men') {
      return imageInfo.includes('men') && !imageInfo.includes('women');
    }
    return false;
  }

  // Function to extract number from filename
  function extractImageNumber(element) {
    const img = element.querySelector('img');
    if (!img) return 999; // Default high number for items without images
    
    const imgSrc = img.src || img.getAttribute('srcset') || '';
    const imgAlt = img.alt || '';
    
    // Look for pattern like "_1_", "_2", etc. in filename
    const numberMatch = (imgSrc + ' ' + imgAlt).match(/_(\d+)/);
    return numberMatch ? parseInt(numberMatch[1]) : 999;
  }

  const primaryCategory = isFromWomen ? 'women' : 'men';
  const secondaryCategory = isFromWomen ? 'men' : 'women';

  const primaryGenderImages = [];
  const secondaryGenderImages = [];
  const productImages = [];

  mediaItems.forEach((item, index) => {
    if (shouldPrioritize(item, primaryCategory)) {
      primaryGenderImages.push({
        element: item,
        number: extractImageNumber(item),
        thumbnailElement: thumbnailItems[index] || null,
        originalIndex: index,
        gender: primaryCategory
      });
    } else if (shouldPrioritize(item, secondaryCategory)) {
      secondaryGenderImages.push({
        element: item,
        number: extractImageNumber(item),
        thumbnailElement: thumbnailItems[index] || null,
        originalIndex: index,
        gender: secondaryCategory
      });
    } else {
      // Add items that don't match either gender to productImages
      productImages.push({
        element: item,
        number: extractImageNumber(item),
        thumbnailElement: thumbnailItems[index] || null,
        originalIndex: index,
        gender: 'neutral'
      });
    }
  });

  // Sort all arrays by number
  primaryGenderImages.sort((a, b) => a.number - b.number);
  secondaryGenderImages.sort((a, b) => a.number - b.number);
  productImages.sort((a, b) => a.number - b.number);

  // Handle case where there are no primary gender images FOR THE CURRENT VARIANT - promote secondary to primary
  let finalPrimaryImages = [...primaryGenderImages];
  let finalSecondaryImages = [...secondaryGenderImages];
  
  if (primaryGenderImages.length === 0 && secondaryGenderImages.length > 0) {
    // console.log('=== NO PRIMARY IMAGES FOR CURRENT VARIANT - PROMOTING SECONDARY TO PRIMARY ===');
    // console.log(`Originally looking for: ${primaryCategory} images`);
    // console.log(`No ${primaryCategory} images found in current variant, but found ${secondaryGenderImages.length} ${secondaryCategory} images`);
    
    finalPrimaryImages = secondaryGenderImages.map(img => ({
      ...img,
      originalGender: img.gender, // Keep track of original gender
      promotedToPrimary: true,
      promotionReason: 'variant-missing-primary'
    }));
    finalSecondaryImages = []; // Clear secondary since they're now primary
    // console.log(`Promoted ${finalPrimaryImages.length} ${secondaryCategory} images to primary for current variant`);
  }

  // console.log('=== VARIANT-AWARE ARRAY CONTENTS DEBUG ===');
  // console.log(`Entry point: ${isFromWomen ? 'Women' : 'Men'} collection`);
  // console.log(`Looking for primary: ${primaryCategory}, secondary: ${secondaryCategory}`);
  // console.log('Final Primary Gender Images (' + finalPrimaryImages.length + '):', 
  //   finalPrimaryImages.map(img => ({
  //     number: img.number,
  //     gender: img.originalGender || img.gender,
  //     originalIndex: img.originalIndex,
  //     promoted: img.promotedToPrimary || false,
  //     reason: img.promotionReason || 'normal'
  //   }))
  // );

  // console.log('Final Secondary Gender Images (' + finalSecondaryImages.length + '):', 
  //   finalSecondaryImages.map(img => ({
  //     number: img.number,
  //     gender: img.gender,
  //     originalIndex: img.originalIndex
  //   }))
  // );

  // console.log('Product Images (' + productImages.length + '):', 
  //   productImages.map(img => ({
  //     number: img.number,
  //     gender: img.gender,
  //     originalIndex: img.originalIndex
  //   }))
  // );

  // Create zigzag pattern with proper priority and sorting
  function createZigzagPattern() {
    const pattern = [];
    
    // Combine final primary gender and product images, then sort by index number
    const primaryAndProductImages = [...finalPrimaryImages, ...productImages];
    
    // Sort by index number (lowest first) - this ensures proper diagonal arrangement
    primaryAndProductImages.sort((a, b) => a.number - b.number);
    
    // console.log('=== PRIORITY SORTING DEBUG ===');
    // console.log('Combined Primary + Product (sorted by index):', 
    //   primaryAndProductImages.map(img => ({
    //     type: finalPrimaryImages.includes(img) ? 'PRIMARY' : 'PRODUCT',
    //     number: img.number,
    //     gender: img.gender,
    //     promoted: img.promotedToPrimary || false
    //   }))
    // );
    
    // Add the sorted primary and product images first
    primaryAndProductImages.forEach((imgData, index) => {
      const patternType = finalPrimaryImages.includes(imgData) ? 'primary' : 'product';
      pattern.push({...imgData, patternType: patternType, patternPosition: index});
    });
    
    // Add all remaining secondary gender images at the end (if any)
    finalSecondaryImages.forEach((imgData, index) => {
      const finalPosition = primaryAndProductImages.length + index;
      pattern.push({...imgData, patternType: 'secondary', patternPosition: finalPosition});
    });

    // console.log('=== FINAL PATTERN ORDER FOR CURRENT VARIANT ===');
    pattern.forEach((img, index) => {
      const promotedText = img.promotedToPrimary ? ` (PROMOTED ${img.originalGender || img.gender})` : '';
      // console.log(`Position ${index}: ${img.patternType.toUpperCase()}${promotedText} - ${img.originalGender || img.gender} - index ${img.number}`);
    });

    return pattern;
  }

  const zigzagPattern = createZigzagPattern();
  
  // console.log('=== ZIGZAG PATTERN DEBUG ===');
  // zigzagPattern.forEach((img, index) => {
  //   console.log(`Position ${index}: ${img.patternType} - ${img.gender} - number ${img.number}`);
  // });

  // Only proceed if we have images to reorder
  if (zigzagPattern.length === 0) return;

  const viewerContainer = viewer.querySelector('.slider') || viewer;
  const thumbnailContainer = thumbnails ? (thumbnails.querySelector('.slider') || thumbnails) : null;

  // console.log('=== INSERTION DEBUG ===');
  // console.log('Viewer container children before:', viewerContainer.children.length);
  // console.log('Thumbnail container children before:', thumbnailContainer ? thumbnailContainer.children.length : 'No thumbnails');

  // Store reference to all current children before removing
  const currentViewerChildren = Array.from(viewerContainer.children);
  const currentThumbnailChildren = thumbnailContainer ? Array.from(thumbnailContainer.children) : [];

  // Remove only the images that will be reordered (not all images)
  const allImagesToReorder = [...primaryGenderImages, ...secondaryGenderImages, ...productImages];
  allImagesToReorder.forEach(imgData => {
    imgData.element.remove();
    if (imgData.thumbnailElement) {
      imgData.thumbnailElement.remove();
    }
  });

  // console.log('After removal - Viewer children:', viewerContainer.children.length);
  // console.log('After removal - Thumbnail children:', thumbnailContainer ? thumbnailContainer.children.length : 'No thumbnails');

  // Clear the containers completely and rebuild in correct order
  viewerContainer.innerHTML = '';
  if (thumbnailContainer) {
    thumbnailContainer.innerHTML = '';
  }

  // Insert each image in the zigzag pattern order
  zigzagPattern.forEach((imgData, index) => {
    // Insert in viewer
    viewerContainer.appendChild(imgData.element);
    
    // Insert thumbnail if it exists
    if (thumbnailContainer && imgData.thumbnailElement) {
      thumbnailContainer.appendChild(imgData.thumbnailElement);
    }
    
    const promotedText = imgData.promotedToPrimary ? ' (promoted from secondary)' : '';
    // console.log(`Inserted ${imgData.patternType} (${imgData.gender})${promotedText} image ${imgData.number} at position ${index}`);
  });

  // console.log(`Created zigzag pattern for ${primaryCategory} priority`);
  // console.log('Final pattern:', zigzagPattern.map(img => `${img.patternType}(${img.number})`).join(' -> '));
}

const previousURL = document.referrer;
// console.log(previousURL, 'Hehey');

// Run reordering when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    reorderMediaBasedOnReferrer();
    setupVariantChangeListener();
  });
} else {
  reorderMediaBasedOnReferrer();
  setupVariantChangeListener();
}